import React from 'react';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { SkeletonItem } from '@helpcenter/skeleton-item';
import { injectIntl } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useExternalLinkIcon } from 'view/external-links/use-external-link-icon';
import type { Size } from '@atlaskit/icon';
import BookIcon from '@atlaskit/icon/glyph/book';
import HipchatLobbyIcon from '@atlaskit/icon/glyph/hipchat/lobby';
import QuestionsIcon from '@atlaskit/icon/glyph/questions';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import messages from './messages';
import { iconDimension } from './styled';
import type { iconTypes, Props } from './types';

const getFallbackIcon = ({ iconType, intl, size }: { iconType: iconTypes; intl: IntlShape; size: Size }) => {
    const icon = token('color.icon', colors.N500);
    switch (iconType) {
        case 'REQUEST_FORM': {
            return (
                <HipchatLobbyIcon
                    label={intl.formatMessage(messages.externalResourceRequestFormIconLabel)}
                    size={size}
                    primaryColor={icon}
                />
            );
        }
        case 'ARTICLE': {
            return (
                <BookIcon
                    label={intl.formatMessage(messages.externalResourceArticleIconLabel)}
                    size={size}
                    primaryColor={icon}
                />
            );
        }
        case 'CHANNEL':
        default: {
            return (
                <QuestionsIcon
                    label={intl.formatMessage(messages.externalResourceHelpChannelIconLabel)}
                    size={size}
                    primaryColor={icon}
                />
            );
        }
    }
};

const getIcon = ({
    isIconLoading,
    iconUrl,
    resourceType,
    intl,
    size = 'large',
}: {
    isIconLoading: boolean;
    iconUrl: string;
    resourceType: iconTypes;
    intl: IntlShape;
    size?: Size;
}) => {
    if (isIconLoading) {
        return <SkeletonItem height={iconDimension} width={iconDimension} />;
    }
    if (iconUrl) {
        return <AutoScaledImage src={iconUrl} inheritWidth draggable={false} />;
    }
    return getFallbackIcon({ intl, size, iconType: resourceType });
};

const ExternalResourceIcon: React.FC<Props> = (props: Props) => {
    di(useExternalLinkIcon);

    const { data, isLoading } = useExternalLinkIcon({
        isLoggedIn: props.isUserLoggedIn,
        link: props.iconUrl || '',
    });

    return getIcon({
        isIconLoading: isLoading,
        iconUrl: data || '',
        resourceType: props.resourceType,
        intl: props.intl,
        size: props.size,
    });
};

export default injectIntl(ExternalResourceIcon);
